import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LogOut, Moon, Settings, Shield, Sun, User } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useApi } from "../context/ApiContext";
import { signOut } from "@/api/session";
import GlobalSearch from "./GlobalSearch";
import { TopNav, TradesLink } from "./Sidebar";
import { initials } from "@/lib/user";

type Crumb = { label: string; to?: string };

function nameOf(rows: { id: string; name?: string }[], id: string): string {
  return rows.find((r) => r.id === id)?.name?.trim() || "…";
}

/** Section / page trail for the current route, e.g. Backtests › EMA Crossover. */
function useCrumbs(): Crumb[] {
  const { pathname } = useLocation();
  const [, a, b, c] = pathname.split("/");
  const api = useApi(a === "integrations" && c ? ["accounts"] : a === "lab" || a === "live" ? ["backtests"] : []);

  if (!a) return [{ label: "Dashboard" }];
  if (a === "lab") {
    const root = { label: "Backtests", to: "/lab/backtest" };
    if (!c) return [{ label: "Backtests" }];
    if (c === "new") return [root, { label: "New backtest" }];
    return [root, { label: nameOf(api.backtests, c) }];
  }
  if (a === "live") {
    const root = { label: "Live", to: "/live" };
    if (!b) return [{ label: "Live" }];
    if (b === "new") return [root, { label: "Go live" }];
    return [root, { label: nameOf(api.backtests, b) }];
  }
  if (a === "integrations") {
    const root = { label: "Accounts", to: "/integrations/marketplaces" };
    // "New account" is a dialog over the list.
    if (!c || c === "new") return [{ label: "Accounts" }];
    return [root, { label: nameOf(api.accounts, c) }];
  }
  if (a === "trades" || a === "orders") return [{ label: "Trades" }];
  if (a === "account") {
    const page = { profile: "Profile", security: "Security", preferences: "Preferences" }[b || ""];
    return [{ label: "Settings" }, ...(page ? [{ label: page }] : [])];
  }
  return [];
}

const MENU = [
  { to: "/account/profile", label: "Profile", icon: User },
  { to: "/account/security", label: "Security", icon: Shield },
  { to: "/account/preferences", label: "Preferences", icon: Settings },
];

export default function Topbar() {
  const api = useApi([]);
  const crumbs = useCrumbs();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  async function handleLogout() {
    // Ends the SSO session too, or the next sign-in reuses the same account.
    await signOut().catch((err: unknown) => console.error("logout failed", err));
  }

  const displayName = api.user?.name?.trim() || api.user?.email || "Account";
  const { theme, toggle: toggleTheme } = useTheme();

  return (
    <header className="flex min-h-11 shrink-0 items-center gap-2.5 border-b border-[var(--border)] bg-[var(--bg-app)] pt-[env(safe-area-inset-top)] pl-[max(0.625rem,env(safe-area-inset-left))] pr-[max(0.625rem,env(safe-area-inset-right))] md:px-4 md:py-2.5">
      {/* The logo is the way to the dashboard; the menu has no Dashboard item. */}
      <Link
        to="/"
        className="flex h-11 shrink-0 items-center gap-2 text-[var(--text-primary)] no-underline md:mr-3 md:h-8"
        title="Dashboard"
        aria-label="TradeStorm dashboard"
      >
        <img src="/tradestorm-logo.svg" alt="" className="h-7 w-auto object-contain" />
        <span className="hidden text-[15px] font-semibold tracking-[-0.01em] sm:inline">TradeStorm</span>
      </Link>

      <TopNav />

      {/* On wide screens the menu already says where you are; the trail shows only below a section. */}
      <nav
        aria-label="Breadcrumb"
        className={`min-w-0 flex-1 ${crumbs.length > 1 ? "md:ml-2 md:border-l md:border-[var(--border)] md:pl-3" : "md:invisible"}`}
      >
        <ol className="m-0 flex min-w-0 list-none items-center gap-1 p-0 text-[13px]">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <li key={i} className={`flex min-w-0 items-center gap-1.5 ${last ? "" : "shrink-0"}`}>
                {i > 0 && <span className="text-[var(--text-muted)]">/</span>}
                {c.to && !last ? (
                  <Link to={c.to} className="text-[var(--text-muted)] no-underline hover:text-[var(--text-primary)]">
                    {c.label}
                  </Link>
                ) : (
                  <span
                    aria-current={last ? "page" : undefined}
                    className={`truncate ${last ? "font-semibold text-[var(--text-primary)]" : "text-[var(--text-muted)]"}`}
                  >
                    {c.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="flex shrink-0 items-center gap-1.5">
        <GlobalSearch />

        <TradesLink />

        <button
          type="button"
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          title={theme === "dark" ? "Light theme" : "Dark theme"}
          className="flex h-11 w-11 items-center justify-center rounded-full text-[var(--text-muted)] hover:text-[var(--text-primary)] md:h-[26px] md:w-[26px] md:hover:bg-[var(--bg-hover)]"
          onClick={toggleTheme}
        >
          {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={displayName}
            className="flex h-11 w-11 items-center justify-center rounded-full md:h-[26px] md:w-[26px]"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-card-elevated)] text-[11px] font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-hover)]">
              {initials(api.user)}
            </span>
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 z-40 mt-1.5 max-h-[calc(100dvh-4.5rem)] w-60 overflow-y-auto overflow-x-hidden rounded-card border border-[var(--border)] bg-[var(--bg-card)] shadow-md"
            >
              <div className="flex items-center gap-2.5 border-b border-[var(--divider)] px-3 py-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--bg-card-elevated)] text-[11px] font-semibold text-[var(--text-primary)]">
                  {initials(api.user)}
                </span>
                <div className="min-w-0">
                  {api.user?.name?.trim() && (
                    <div className="truncate text-sm font-semibold text-[var(--text-primary)]">{api.user.name.trim()}</div>
                  )}
                  <div className="truncate text-xs text-[var(--text-muted)]">{api.user?.email || "Account"}</div>
                </div>
              </div>
              <div className="py-1">
                {MENU.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    role="menuitem"
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2.5 text-sm no-underline hover:bg-[var(--bg-hover)] ${
                        isActive ? "font-medium text-[var(--text-primary)]" : "text-[var(--text-secondary)]"
                      }`
                    }
                    onClick={() => setMenuOpen(false)}
                  >
                    <Icon size={15} className="shrink-0" />
                    {label}
                  </NavLink>
                ))}
              </div>
              <div className="border-t border-[var(--divider)] py-1">
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-[var(--danger)] hover:bg-[var(--bg-hover)]"
                  onClick={handleLogout}
                >
                  <LogOut size={15} className="shrink-0" />
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
